import * as React from 'react';
import { FormContextValues, useForm } from 'react-hook-form';

const localStringToNumber = (s: string): number => {
  const numberRegEx = /[^0-9.-]+/g;
  const commasRegEx = /,/g;
  const cleaned = s.replace(numberRegEx, '').replace(commasRegEx, '');
  return Number(cleaned);
};

const formatAmount = (amount: string) => {
  const options = {
    currency: 'USD',
    style: 'decimal',
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  };

  return localStringToNumber(amount).toLocaleString(undefined, options);
};
interface Props {
  onSubmit: (data: RecordOfflinePaymentFormValues) => void;
}

export type RecordOfflinePaymentFormValues = { paymentType: string; amount: string; note: string };

export default function useRecordOfflinePayment({
  onSubmit,
}: Props): [FormContextValues<RecordOfflinePaymentFormValues>, () => void, (name: string) => string[]] {
  const [hasUserChangedNotes, setHasUserChangedNotes] = React.useState(false);
  const recordOfflinePaymentForm = useForm({
    mode: 'onBlur',
    defaultValues: {
      paymentType: 'Cash',
      amount: '',
      note: '',
    },
  });

  const { handleSubmit, errors, watch, setValue } = recordOfflinePaymentForm;

  const watchFields = watch(['paymentType', 'amount']);
  const watchNote = watch('note');

  React.useEffect(() => {
    if (watchFields.paymentType && watchFields.amount && !hasUserChangedNotes) {
      setValue('note', `${watchFields.paymentType} payment recorded: $${formatAmount(watchFields.amount)}`);
    }
  }, [watchFields.paymentType, watchFields.amount]);

  React.useEffect(() => {
    if (
      watchNote &&
      watchNote !== `${watchFields.paymentType} payment recorded: $${formatAmount(watchFields.amount)}`
    ) {
      setHasUserChangedNotes(true);
    }
  }, [watchNote]);

  const getFormErrors = (name: string): string[] => (errors[name] ? [errors[name].message] : null);

  const submitOfflinePayment = handleSubmit((data) => {
    setHasUserChangedNotes(false);
    onSubmit(data)
  });

  return [recordOfflinePaymentForm, submitOfflinePayment, getFormErrors];
}
